import { useState } from "react"
import { Link, useParams, useNavigate } from "react-router-dom"
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query"
import {
  ArrowLeft,
  Loader2,
  User,
  DollarSign,
  Trash2,
  Check,
  X,
  Pencil,
} from "lucide-react"
import { AppShell } from "@/components/AppShell"
import { Card } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import { dealsApi, type DealStatus, type PipelineStage } from "@/lib/api"
import { CommunicationTimeline } from "@/components/CommunicationTimeline"
import { ConfirmDeleteDialog } from "@/components/ConfirmDeleteDialog"
import { SettledEnrolDialog } from "@/components/SettledEnrolDialog"
import { Combobox } from "@/components/ui/combobox"
// LENDERS free-text list is no longer used — lender picker uses the real
// lender directory + logos from the CRM database.
// import { LENDERS } from "@/pages/deals/lenders"
import { LenderLogo } from "@/components/LenderLogo"
import { LenderSelector } from "@/components/LenderSelector"
import type { Lender } from "@/lib/api"
import { ASSET_TYPES } from "@/pages/deals/asset-types"
import { formatCurrency } from "@shared/utils/currency"
import { formatDate, formatDateTime, timeAgo } from "@shared/utils/date"
import { useAuth } from "@/hooks/useAuth"

const STAGE_LABEL: Record<PipelineStage, string> = {
  new_lead: "New Lead",
  day_one: "Day 1",
  day_two: "Day 2",
  day_three: "Day 3",
  general_followup: "General Follow-up",
  awaiting_docs: "Awaiting Docs",
  docs_received: "Docs Received",
  approved: "Approved",
  settled: "Settled",
  closed: "Closed",
}
const STATUS_LABEL: Record<DealStatus, string> = {
  new_lead: "New Lead",
  awaiting_docs: "Awaiting Docs",
  docs_received: "Docs Received",
  approved: "Approved",
  settled: "Settled",
  closed: "Closed",
  lost: "Lost",
  on_hold: "On Hold",
}

export default function DealDetailPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const qc = useQueryClient()
  const { user } = useAuth()
  const [deleteOpen, setDeleteOpen] = useState(false)

  const deal = useQuery({
    queryKey: ["deal", id],
    queryFn: () => dealsApi.get(id!),
    enabled: !!id,
  })

  // Lead-source dropdown options — merged built-in canonical sources plus
  // any custom values already used by brokers in this org, sorted by usage.
  const leadSourcesQ = useQuery({
    queryKey: ["deals-lead-sources"],
    queryFn: () => dealsApi.leadSources(),
    staleTime: 60_000,
  })
  const leadSourceOptions =
    leadSourcesQ.data?.sources.map((s) => ({
      value: s.value,
      label: s.value,
    })) ?? []

  const [settledDialog, setSettledDialog] = useState<{
    contactId: string
    automationIds: string[]
  } | null>(null)

  const move = useMutation({
    mutationFn: (stage: PipelineStage) => dealsApi.move(id!, stage),
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: ["deal", id] })
      qc.invalidateQueries({ queryKey: ["deals-pipeline"] })
      // If the deal transitioned INTO settled, open the enrol dialog.
      const contactId = deal.data?.deal?.contactId
      if (
        res.transitionedIntoSettled &&
        res.settledEnrolAutomationIds &&
        res.settledEnrolAutomationIds.length > 0 &&
        contactId
      ) {
        setSettledDialog({
          contactId,
          automationIds: res.settledEnrolAutomationIds,
        })
      }
    },
  })

  const remove = useMutation({
    mutationFn: () => dealsApi.remove(id!),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["deals"] })
      navigate("/deals", { replace: true })
    },
  })

  if (deal.isLoading) {
    return (
      <AppShell title="Deal">
        <div className="text-muted-foreground grid place-items-center py-24">
          <Loader2 className="h-6 w-6 animate-spin" />
        </div>
      </AppShell>
    )
  }
  if (deal.error || !deal.data || !deal.data.deal) {
    return (
      <AppShell title="Deal">
        <Card className="p-8 text-center">
          <p className="text-destructive">Deal not found or you don't have access.</p>
          <Button asChild variant="outline" className="mt-4">
            <Link to="/deals">Back to deals</Link>
          </Button>
        </Card>
      </AppShell>
    )
  }

  const d = deal.data.deal
  const contactName =
    d.contact?.businessName ?? (d.contact ? `${d.contact.firstName} ${d.contact.lastName}` : "—")
  const amount = d.naf ?? d.loanAmount
  const canDelete = user && ["admin", "manager"].includes(user.role)

  return (
    <AppShell title="">
      <div className="mb-4">
        <Link
          to="/deals"
          className="text-muted-foreground hover:text-foreground inline-flex items-center gap-1 text-sm"
        >
          <ArrowLeft className="h-4 w-4" />
          Deals
        </Link>
      </div>

      <Card className="mb-6 p-6">
        <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
          <div className="flex items-start gap-4">
            {(d.lenderName || d.lender) && (
              <LenderLogo
                name={d.lenderName ?? d.lender ?? ""}
                logoUrl={d.lenderLogoUrl}
                size="lg"
              />
            )}
            <div>
              <div className="flex items-center gap-2 flex-wrap">
                <h1 className="text-2xl font-semibold tracking-tight">
                  {d.dealName ?? "Untitled deal"}
                </h1>
                <Badge>{STATUS_LABEL[d.status as DealStatus]}</Badge>
                {d.priority === "high" && <Badge variant="destructive">High Priority</Badge>}
              </div>
              <p className="text-muted-foreground mt-1 text-sm">
                <Link to={`/contacts/${d.contact?.id}`} className="hover:text-foreground">
                  {contactName}
                </Link>
                {(d.lenderName || d.lender) && <> · {d.lenderName ?? d.lender}</>}
              </p>
            </div>
          </div>

          <div className="flex gap-2">
            {canDelete && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setDeleteOpen(true)}
                className="text-destructive"
                title="Delete deal"
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            )}
          </div>
        </div>

        {/* Stage picker */}
        <div className="border-border mt-6 border-t pt-4">
          <Label className="text-muted-foreground text-xs uppercase tracking-wider">
            Pipeline Stage
          </Label>
          <div className="mt-2 flex flex-wrap gap-2">
            {(Object.keys(STAGE_LABEL) as PipelineStage[]).map((stage) => (
              <button
                key={stage}
                onClick={() => move.mutate(stage)}
                disabled={move.isPending || d.pipelineStage === stage}
                className={`rounded-md px-3 py-1.5 text-xs font-medium transition-colors ${
                  d.pipelineStage === stage
                    ? "bg-rc-blue-600 text-white"
                    : "border-border bg-background hover:bg-accent border"
                }`}
              >
                {STAGE_LABEL[stage]}
              </button>
            ))}
          </div>
        </div>

        {/* Compact meta row — no double-up with Deal Information card below */}
        <div className="border-border mt-6 flex flex-wrap items-center gap-4 border-t pt-4 text-sm">
          <div className="flex items-center gap-2">
            <User className="text-muted-foreground h-4 w-4" />
            <span className="text-muted-foreground">Owner:</span>
            <span className="text-foreground font-medium">{d.ownerName ?? "Unassigned"}</span>
          </div>
          {amount != null && (
            <div className="flex items-center gap-2">
              <DollarSign className="text-muted-foreground h-4 w-4" />
              <span className="text-foreground font-semibold">{formatCurrency(amount)}</span>
            </div>
          )}
        </div>
      </Card>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <div className="lg:col-span-2">
          <Card className="p-5">
            <h3 className="text-foreground mb-4 text-sm font-semibold">Deal Information</h3>
            <dl className="grid grid-cols-1 gap-3 text-sm md:grid-cols-2">
              <EditableRow
                dealId={d.id}
                label="Finance Type"
                field="financeType"
                value={d.financeType}
                kind="select"
                options={[
                  { value: "consumer", label: "Consumer" },
                  { value: "commercial", label: "Commercial" },
                ]}
              />
              <EditableRow
                dealId={d.id}
                label="Asset Type"
                field="financeCategory"
                value={d.financeCategory}
                kind="combobox"
                options={ASSET_TYPES.map((n) => ({ value: n, label: n }))}
              />
              <EditableRow
                dealId={d.id}
                label="Lender"
                field="lender"
                value={d.lenderName ?? d.lender}
                kind="lender"
                lenderId={d.lenderId ?? null}
                lenderLogoUrl={d.lenderLogoUrl}
                financeTypeFilter={d.financeType}
              />
              <EditableRow
                dealId={d.id}
                label="NAF"
                field="naf"
                value={d.naf}
                kind="currency"
                hint="Net Amount Financed"
              />
              <EditableRow
                dealId={d.id}
                label="Balloon Amount"
                field="balloon"
                value={d.balloon}
                kind="currency"
              />
              <EditableRow
                dealId={d.id}
                label="Term End Date"
                field="termEndDate"
                value={d.termEndDate}
                kind="date"
                hint="When the loan ends. If balloon > 0, the balloon is due on this date. Drives term_end automations."
              />
              <EditableRow
                dealId={d.id}
                label="Settled Date"
                field="settledDate"
                value={d.settledDate}
                kind="date"
                hint="Used by post-settlement automations"
              />
              <EditableRow
                dealId={d.id}
                label="Term (months)"
                field="termMonths"
                value={d.termMonths}
                kind="integer"
              />
              <EditableRow
                dealId={d.id}
                label="Writing Rate"
                field="writingRate"
                value={d.writingRate}
                kind="percent"
              />
              <EditableRow
                dealId={d.id}
                label="Commission"
                field="commission"
                value={d.commission}
                kind="currency"
              />
              <EditableRow
                dealId={d.id}
                label="Lead Source"
                field="leadSource"
                value={d.leadSource ?? d.sourceProvider}
                kind="combobox"
                options={leadSourceOptions}
                creatable
                createLabel="Add source"
                hint={
                  d.leadSource == null && d.sourceProvider
                    ? `Legacy value: ${d.sourceProvider} — pick one to update`
                    : undefined
                }
              />
              <EditableRow
                dealId={d.id}
                label="Deal Name"
                field="dealName"
                value={d.dealName}
                kind="text"
              />
            </dl>
            {d.notes && (
              <div className="border-border mt-4 border-t pt-4">
                <div className="text-muted-foreground text-xs uppercase tracking-wider">Notes</div>
                <p className="mt-1 whitespace-pre-wrap text-sm">{d.notes}</p>
              </div>
            )}
          </Card>

          {d.contact?.id && (
            <Card className="mt-6 p-5">
              <CommunicationTimeline
                contactId={d.contact.id}
                dealId={d.id}
                showAddNote
                title="Communications for this Deal"
              />
            </Card>
          )}
        </div>

        <Card className="p-5">
          <h3 className="text-foreground mb-3 text-sm font-semibold">Pipeline History</h3>
          {deal.data.history.length === 0 ? (
            <p className="text-muted-foreground text-sm">No stage changes yet.</p>
          ) : (
            <ol className="space-y-3">
              {deal.data.history.map((h) => (
                <li key={h.id} className="border-rc-blue-200 border-l-2 pl-3 text-sm">
                  <div className="text-foreground font-medium">
                    {h.fromStage
                      ? `${STAGE_LABEL[h.fromStage as PipelineStage] ?? h.fromStage} → ${STAGE_LABEL[h.toStage as PipelineStage] ?? h.toStage}`
                      : `Created at ${STAGE_LABEL[h.toStage as PipelineStage] ?? h.toStage}`}
                  </div>
                  <div className="text-muted-foreground text-xs">
                    {formatDateTime(h.createdAt)} · {timeAgo(h.createdAt)}
                  </div>
                </li>
              ))}
            </ol>
          )}
        </Card>
      </div>

      {/* Type-to-confirm delete dialog. Broker must type the deal name to
          unlock the Delete button. Prevents accidental clicks in a workflow
          where deal deletion is rare but destructive. */}
      <ConfirmDeleteDialog
        open={deleteOpen}
        onOpenChange={setDeleteOpen}
        title="Delete deal"
        description={
          <>
            You're about to permanently delete{" "}
            <strong>{d.dealName ?? "this deal"}</strong>. This cannot be undone.
          </>
        }
        additionalWarning={
          <>
            <div>• Pipeline history for this deal will be deleted</div>
            <div>• Any linked automation runs will be cancelled</div>
            <div>
              • The contact record{d.contact ? ` (${d.contact.firstName} ${d.contact.lastName})` : ""} will remain intact
            </div>
          </>
        }
        confirmationText={d.dealName || "DELETE"}
        onConfirm={async () => {
          await remove.mutateAsync()
        }}
        destructiveLabel="Delete deal"
      />

      {/* On-settled enrol dialog. Opens automatically after move() when
          the deal transitions into settled (backend returns
          transitionedIntoSettled + settledEnrolAutomationIds). */}
      {settledDialog && (
        <SettledEnrolDialog
          open={true}
          onOpenChange={(o) => {
            if (!o) setSettledDialog(null)
          }}
          dealId={id!}
          contactId={settledDialog.contactId}
          automationIds={settledDialog.automationIds}
        />
      )}
    </AppShell>
  )
}

// ============================================================
// EditableRow — inline editing for any deal field.
// Click the value → becomes an input → save on blur / Enter / cancel on Esc.
// ============================================================
type EditableKind =
  | "text"
  | "currency"
  | "integer"
  | "percent"
  | "date"
  | "select"
  | "combobox"
  | "lender"

function EditableRow({
  dealId,
  label,
  field,
  value,
  kind,
  hint,
  options,
  creatable,
  createLabel,
  lenderId,
  lenderLogoUrl,
  financeTypeFilter,
}: {
  dealId: string
  label: string
  field: string
  value: string | number | null | undefined
  kind: EditableKind
  hint?: string
  options?: Array<{ value: string; label: string }>
  // Combobox-specific: allow the user to add a value not already in options
  creatable?: boolean
  createLabel?: string
  // Lender-picker specific props
  lenderId?: string | null
  lenderLogoUrl?: string | null
  financeTypeFilter?: "consumer" | "commercial"
}) {
  const qc = useQueryClient()
  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState<string>(rawInputValue(value, kind))
  const [error, setError] = useState<string | null>(null)

  const save = useMutation({
    mutationFn: async (nextValue: string | number | null) => {
      return dealsApi.update(dealId, { [field]: nextValue } as never)
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["deal", dealId] })
      qc.invalidateQueries({ queryKey: ["deals"] })
      qc.invalidateQueries({ queryKey: ["deals-pipeline"] })
      setEditing(false)
      setError(null)
    },
    onError: (e: unknown) => {
      const msg = e instanceof Error ? e.message : "Save failed"
      setError(msg)
    },
  })

  function commit() {
    setError(null)
    const parsed = parseInput(draft, kind)
    if (parsed.error) {
      setError(parsed.error)
      return
    }
    save.mutate(parsed.value)
  }

  function cancel() {
    setDraft(rawInputValue(value, kind))
    setError(null)
    setEditing(false)
  }

  function handleKey(e: React.KeyboardEvent) {
    if (e.key === "Enter" && kind !== "select") {
      e.preventDefault()
      commit()
    } else if (e.key === "Escape") {
      e.preventDefault()
      cancel()
    }
  }

  // Lender-kind rows use a dedicated inline surface (always visible — no
  // click-to-edit state) since the LenderSelector is already a
  // click-to-open picker.
  if (kind === "lender") {
    return (
      <div className="group">
        <dt className="text-muted-foreground text-xs uppercase tracking-wider flex items-center gap-1">
          {label}
        </dt>
        <dd className="mt-1 text-foreground text-sm font-medium">
          <LenderSelector
            value={lenderId ?? null}
            financeFilter={financeTypeFilter ?? "all"}
            onChange={(l: Lender | null) => {
              setError(null)
              // Update both `lender` (free-text, for legacy displays)
              // and `lenderId` (foreign key, drives the logo) in a
              // single PATCH so header + logo update together.
              dealsApi
                .update(dealId, {
                  lender: l?.name ?? null,
                  lenderId: l?.id ?? null,
                } as never)
                .then(() => {
                  qc.invalidateQueries({ queryKey: ["deal", dealId] })
                  qc.invalidateQueries({ queryKey: ["deals"] })
                  qc.invalidateQueries({ queryKey: ["deals-pipeline"] })
                })
                .catch((e: unknown) => {
                  setError(e instanceof Error ? e.message : "Save failed")
                })
            }}
            placeholder="Select a lender…"
          />
          {error && (
            <div className="text-destructive mt-1 text-xs">{error}</div>
          )}
        </dd>
      </div>
    )
  }

  // Silence unused-var warnings for props that only apply to kind="lender".
  void lenderLogoUrl

  return (
    <div className="group">
      <dt className="text-muted-foreground text-xs uppercase tracking-wider flex items-center gap-1">
        {label}
        {hint && (
          <span
            title={hint}
            className="cursor-help text-[10px] text-muted-foreground/70"
          >
            ⓘ
          </span>
        )}
      </dt>
      <dd className="text-foreground text-sm font-medium">
        {editing ? (
          <div className="mt-0.5 flex items-center gap-1">
            {kind === "combobox" ? (
              <div className="flex-1">
                <Combobox
                  options={options ?? []}
                  value={draft || null}
                  onChange={(v) => {
                    setDraft(v ?? "")
                    // Auto-save on select for combobox (feels natural).
                    setError(null)
                    save.mutate((v ?? null) as string | null)
                  }}
                  placeholder="Type to search…"
                  creatable={creatable}
                  createLabel={createLabel}
                />
              </div>
            ) : kind === "select" ? (
              <select
                autoFocus
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                onKeyDown={handleKey}
                className="border-border bg-white h-8 flex-1 rounded border px-2 text-sm"
              >
                <option value="">— clear —</option>
                {options?.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            ) : (
              <input
                autoFocus
                type={kind === "date" ? "date" : "text"}
                inputMode={
                  kind === "currency" || kind === "integer" || kind === "percent"
                    ? "decimal"
                    : "text"
                }
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                onKeyDown={handleKey}
                placeholder={kindPlaceholder(kind)}
                className="border-border bg-white h-8 flex-1 rounded border px-2 text-sm"
              />
            )}
            {kind !== "combobox" && (
              <>
                <button
                  type="button"
                  onClick={commit}
                  disabled={save.isPending}
                  className="hover:bg-emerald-50 text-emerald-700 shrink-0 rounded p-1"
                  title="Save"
                >
                  {save.isPending ? (
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  ) : (
                    <Check className="h-3.5 w-3.5" />
                  )}
                </button>
                <button
                  type="button"
                  onClick={cancel}
                  className="hover:bg-rc-grey-100 text-muted-foreground shrink-0 rounded p-1"
                  title="Cancel"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              </>
            )}
            {kind === "combobox" && (
              <button
                type="button"
                onClick={cancel}
                className="hover:bg-rc-grey-100 text-muted-foreground shrink-0 rounded p-1"
                title="Close"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            )}
          </div>
        ) : (
          <button
            type="button"
            onClick={() => setEditing(true)}
            className="hover:bg-rc-grey-100 -mx-1 -my-0.5 flex w-[calc(100%+8px)] items-center justify-between rounded px-1 py-0.5 text-left transition-colors"
            title="Click to edit"
          >
            <span className={value == null || value === "" ? "text-muted-foreground italic" : ""}>
              {formatDisplay(value, kind, options)}
            </span>
            <Pencil className="text-muted-foreground h-3 w-3 opacity-0 transition-opacity group-hover:opacity-100" />
          </button>
        )}
        {error && (
          <div className="text-destructive mt-1 text-[11px]">{error}</div>
        )}
      </dd>
    </div>
  )
}

function rawInputValue(v: string | number | null | undefined, kind: EditableKind): string {
  if (v == null || v === "") return ""
  if (kind === "currency" || kind === "percent" || kind === "integer") return String(v)
  return String(v)
}

function kindPlaceholder(kind: EditableKind): string {
  switch (kind) {
    case "currency":
      return "e.g. 100000"
    case "integer":
      return "e.g. 60"
    case "percent":
      return "e.g. 7.5"
    case "date":
      return "YYYY-MM-DD"
    default:
      return ""
  }
}

function parseInput(
  raw: string,
  kind: EditableKind
): { error?: string; value: string | number | null } {
  const trimmed = raw.trim()
  if (trimmed === "") return { value: null }
  if (kind === "currency" || kind === "percent") {
    const cleaned = trimmed.replace(/[$,%\s]/g, "")
    const n = Number(cleaned)
    if (!Number.isFinite(n) || n < 0) return { error: "Enter a positive number", value: null }
    return { value: n }
  }
  if (kind === "integer") {
    const n = Number(trimmed)
    if (!Number.isInteger(n) || n < 1) return { error: "Enter a whole number", value: null }
    return { value: n }
  }
  if (kind === "date") {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) return { error: "Use YYYY-MM-DD", value: null }
    return { value: trimmed }
  }
  return { value: trimmed }
}

function formatDisplay(
  v: string | number | null | undefined,
  kind: EditableKind,
  options?: Array<{ value: string; label: string }>
): string {
  if (v == null || v === "") return "— empty —"
  if (kind === "currency") return formatCurrency(Number(v))
  if (kind === "percent") return `${v}%`
  if (kind === "integer") return `${v}`
  if (kind === "date") return formatDate(String(v))
  if (kind === "select" || kind === "combobox") {
    const found = options?.find((o) => o.value === String(v))
    return found?.label ?? String(v)
  }
  return String(v)
}





// Local Label component
function Label({ className = "", children }: { className?: string; children: React.ReactNode }) {
  return <div className={`text-sm font-medium ${className}`}>{children}</div>
}
